import type { PilotComponent } from "~domain/devices/models/pilot/pilot-component-model.ts";
import type {
  PilotConnectionStatus,
  PilotLastConnection,
} from "~domain/devices/models/pilot/pilot-connection-status-model.ts";
import type { PilotDataFrame } from "~domain/devices/models/pilot/pilot-data-frame-model.ts";
import type { PilotGprsConso } from "~domain/devices/models/pilot/pilot-gprs-conso-model.ts";
import type { Pilot, PilotOperatingStatus } from "~domain/devices/models/pilot/pilot-model.ts";
import type { BackofficeFindGprsConsoResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-gprs-conso-response.ts";
import type { BackofficeFindPilotComponentsResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-pilot-components-response.ts";
import type { BackofficeFindPilotDataInfosResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-pilot-data-infos-response.ts";
import type { BackofficeFindPilotLastConnectionResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-pilot-last-connection-response.ts";
import type { BackofficeFindPilotResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-pilot-response.ts";
import type { BackofficeFindSitePilotsResponse } from "~infrastructure/api/backoffice/contract/site/responses/backoffice-find-site-pilots-response.ts";

export const PilotMapper = {
  mapBackofficePilotDataFramesToDomain(
    backofficeDataFrames: BackofficeFindPilotDataInfosResponse,
  ): Array<PilotDataFrame> {
    return backofficeDataFrames.map((backofficeDataFrame) => ({
      id: backofficeDataFrame.id,
      arrivalDate: backofficeDataFrame.arrivalDate,
      frame: backofficeDataFrame.frame,
      pilotHwId: backofficeDataFrame.idHwMombox,
      pilotSentDate: backofficeDataFrame.momboxDate,
      pilotIp: backofficeDataFrame.momboxIp,
      type: backofficeDataFrame.type,
      typeClass: backofficeDataFrame.typeClass,
    }));
  },

  mapBackofficePilotComponentsToDomain(
    backofficePilotComponents: BackofficeFindPilotComponentsResponse,
  ): Array<PilotComponent> {
    return backofficePilotComponents.map((backofficePilotComponent) => ({
      brand: backofficePilotComponent.brand,
      description: backofficePilotComponent.name.description,
      note: backofficePilotComponent.note ?? undefined,
      serial: backofficePilotComponent.serial ?? undefined,
    }));
  },

  mapBackofficePilotLastConnectionToDomain(
    backofficeLastConnection: BackofficeFindPilotLastConnectionResponse,
  ): PilotLastConnection {
    return {
      dateUpdate: backofficeLastConnection.dateUpdate ?? undefined,
      lastIp: backofficeLastConnection.lastIp ?? undefined,
      pilotHwId: backofficeLastConnection.momboxId ?? undefined,
      status: backofficeLastConnection.status as PilotConnectionStatus,
    };
  },

  mapBackofficePilotRefOperatingStatus(refOperatingStatus: number): PilotOperatingStatus {
    switch (refOperatingStatus) {
      case 1:
        return "OK";
      case 2:
        return "WARN";
      case 3:
        return "NOK";
      default:
        return "UNKNOWN";
    }
  },

  mapBackofficePilotGprsConsoToDomain(backofficeGprsConso: BackofficeFindGprsConsoResponse): PilotGprsConso {
    return {
      date: backofficeGprsConso.volumeDate ?? undefined,
      received: backofficeGprsConso.volumeReceived ?? 0,
      sent: backofficeGprsConso.volumeSent ?? 0,
      alert:
        backofficeGprsConso.alertStatus && backofficeGprsConso.alertDate
          ? {
              date: backofficeGprsConso.alertDate,
              status: backofficeGprsConso.alertStatus,
            }
          : undefined,
    };
  },

  mapBackofficePilotToDomain(backofficePilot: BackofficeFindPilotResponse): Pilot | undefined {
    if (!backofficePilot || backofficePilot instanceof Blob) {
      return undefined;
    }

    return {
      confSoftwareId: backofficePilot.confSoftwareId,
      creationDatetime: backofficePilot.csCreationDatetime ?? undefined,
      hardwareVersion: backofficePilot.momboxHardwareVersion.vx ? "vx" : "v2v5",
      csId: backofficePilot.csId ?? undefined,
      hwId: backofficePilot.hwId,
      lastIpDate: backofficePilot.lastIpDate ?? undefined,
      location: backofficePilot.location ?? undefined,
      operatingStatus: PilotMapper.mapBackofficePilotRefOperatingStatus(backofficePilot.refOperatingStatus),
      operator: backofficePilot.operator,
      serialNumber: backofficePilot.serialNumber,
      siteId: backofficePilot.siteId ?? undefined,
      softwareVersion: backofficePilot.softwareVersion,
      updating: backofficePilot.updating,
      needUpdate: false,
    };
  },

  mapBackofficeSitePilotsToDomain(backofficeSitePilots: BackofficeFindSitePilotsResponse): Array<Pilot> {
    return backofficeSitePilots.map((backofficePilot: BackofficeFindSitePilotsResponse[number]) => ({
      hwId: backofficePilot.hwId,
      csId: backofficePilot.csId ?? undefined,
      confSoftwareId: backofficePilot.confSoftwareId,
      creationDatetime: backofficePilot.creationDatetime,
      lastIpDate: backofficePilot.lastIpDate ?? undefined,
      location: backofficePilot.location ?? undefined,
      hardwareVersion: "unknown",
      operator: backofficePilot.operator ?? undefined,
      operatingStatus: PilotMapper.mapBackofficePilotRefOperatingStatus(backofficePilot.refOperatingStatus),
      siteId: backofficePilot.siteId,
      serialNumber: backofficePilot.serialNumber,
      softwareVersion: backofficePilot.softwareVersion,
      updating: backofficePilot.updating,
      needUpdate: backofficePilot.actionToBeTaken?.includes("UPDATE"),
    }));
  },
};
